import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import SkeletonForm from '../../../components/Form/Skeleton'
import SuccessModal from '../../../features/components/Modals/SuccessModal'
import SocialAuthChecker from '../../../features/components/SocialAuthChecker'
import FormDrawerWrapper from '../../../features/components/FormDrawerWrapper'
import RightWrongDatesMessage from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/RightWrongDatesMessage'
import UploadAdCreativeButton from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/UploadAdCreativeButton'
import UploadDV360AdCreativeForm from './UploadDV360AdCreativeForm'
import { ReactComponent as DV360Icon } from '../../../assets/logos/dv360-icon.svg'

import useIsInternalAdCreation from '../../../features/hooks/internalAdHooks/useIsInternalAdCreation'
import useUploadAdCreativeConstants from '../../Multiplatform/UploadAdCreative/useUploadAdCreativeConstants'
import { useExceptedAdsModal } from '../ExceptedAdsModal/useExceptedAdsModal'
import { redirectTo } from '../../../helpers/url'

import { clearRightUploadAdData } from '../../../modules/actions/rights'
import { selectedDV360AdAccountSelector } from '../../../modules/selectors/adAccounts'

import { DV_360_PLATFORM, PLATFORM_LABELS } from '../../../constants/selectLists/platformList'
import { UPLOAD_DV360_AD_CREATIVE } from '../../../constants/forms'
import { ROUTE_PARAMS, ROUTES } from '../../../constants/routes'
import { MODAL_TYPE } from '../../../constants/other'

const initialRedirectionParams = {
  [ROUTE_PARAMS.campaignId]: '',
  [ROUTE_PARAMS.lineItemId]: ''
}

const UploadDV360AdCreative = () => {
  const dispatch = useDispatch()

  const { t } = useTranslation()

  const [redirectionParams, setRedirectionParams] = useState(initialRedirectionParams)

  const { id: selectedDV360AdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const isInternalAdCreation = useIsInternalAdCreation()
  const { isFormLoading, isRightEndDateInThePast } = useUploadAdCreativeConstants()

  const { handleAdsExceptions, ExceptedAdsModal } = useExceptedAdsModal()

  const ButtonTrigger = useCallback(
    props => <UploadAdCreativeButton Icon={DV360Icon} text="Display or Video" {...props} />,
    []
  )

  const modalRedirectionHandler = useCallback(() => {
    redirectTo(ROUTES.lineItemsSummary, {
      ...redirectionParams,
      [ROUTE_PARAMS.platform]: DV_360_PLATFORM,
      [ROUTE_PARAMS.adAccount]: selectedDV360AdAccountId
    })
  }, [redirectionParams, selectedDV360AdAccountId])

  const onModalCloseHandler = useCallback(() => {
    setRedirectionParams(initialRedirectionParams)
  }, [])

  const onAfterFormCloseHandler = useCallback(() => {
    dispatch(clearRightUploadAdData(DV_360_PLATFORM))
  }, [dispatch])

  const renderDrawerContent = useCallback(() => {
    if (isRightEndDateInThePast) {
      return <RightWrongDatesMessage />
    } else {
      return (
        <UploadDV360AdCreativeForm
          setRedirectionParams={setRedirectionParams}
          handleAdsExceptions={handleAdsExceptions}
        />
      )
    }
  }, [isRightEndDateInThePast, handleAdsExceptions])

  return (
    <>
      <FormDrawerWrapper
        formName={UPLOAD_DV360_AD_CREATIVE}
        closeOnSubmit={false}
        OpenDrawerTrigger={ButtonTrigger}
        onAfterFormClose={onAfterFormCloseHandler}
        isFormLoading={isFormLoading}
        SkeletonFormComponent={SkeletonForm}
      >
        <SocialAuthChecker
          platform={DV_360_PLATFORM}
          authFailureText={`Please authenticate with ${PLATFORM_LABELS[DV_360_PLATFORM]} to upload ad creative.`}
        >
          {renderDrawerContent()}
        </SocialAuthChecker>
      </FormDrawerWrapper>
      <SuccessModal
        successButtonText="Go to Line Item"
        successButtonAction={modalRedirectionHandler}
        modalType={MODAL_TYPE.rightDV360AdUploaded}
        onAfterClose={onModalCloseHandler}
        showCloseButton
      >
        <p>
          {t(
            isInternalAdCreation
              ? 'Your ad has been uploaded successfully and is pending review.'
              : 'Your ad has been uploaded successfully.'
          )}
        </p>
      </SuccessModal>
      {ExceptedAdsModal}
    </>
  )
}

export default UploadDV360AdCreative
